/**
 * Generate time options in 5-minute intervals
 */
export const generateTimeOptions = (startHour: number = 8, endHour: number = 22): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({
        value: timeString,
        label: timeString
      });
    }
  }
  
  return options;
};

/**
 * Round a time string to the nearest 5-minute interval
 */
export const roundToNearestFiveMinutes = (timeString: string): string => {
  if (!timeString) return '';
  
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  
  const roundedMinutes = Math.round(minutes / 5) * 5;
  const finalMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;
  const finalHours = roundedMinutes === 60 ? hours + 1 : hours;
  
  return `${finalHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
};

/**
 * Parse a datetime string as local time instead of UTC
 * This mirrors the backend's parseLocalDateTime logic to prevent timezone conversion issues
 * 
 * Backend sends datetime strings like "2025-09-13T15:50:00.000" which should be treated 
 * as local time (Europe/Amsterdam), not UTC
 */
export const parseLocalDateTime = (dateTimeString: string): Date => {
  if (!dateTimeString) return new Date();
  
  // If the string already has timezone info (ends with Z or has offset), use as-is
  if (dateTimeString.endsWith('Z') || dateTimeString.includes('+') || dateTimeString.includes('-')) {
    return new Date(dateTimeString);
  }
  
  // For strings like "2025-09-13T15:50:00.000", JavaScript's Date constructor
  // interprets them as UTC. We need to parse them as local time.
  // Parse the components manually and create a Date in local timezone
  const match = dateTimeString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/);
  if (match) {
    const [, year, month, day, hour, minute, second, millisecond] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1, // JavaScript months are 0-based
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second),
      parseInt(millisecond || '0')
    );
  }
  
  // Fallback to original behavior if parsing fails
  return new Date(dateTimeString);
};

// De opmaakfuncties (formatDate/formatDateTime/formatTime) stonden hier, maar bakten
// Nederlandse dag- en maandnamen én de dag-voor-maand volgorde hard in. Ze zijn vervangen
// door de taalbewuste varianten in `utils/dateFormats.ts`; componenten gebruiken de hook
// `hooks/useDateFormat.ts`. parseLocalDateTime blijft hier: dat is tijdzonelogica.